import React, { useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

const fmt = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('it-IT') : '-';

export const PromoPlatformTasks = ({ data, loading, reload }) => {
  const [busy, setBusy] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const groups = useMemo(() => {
    const grouped = {};
    (data?.tasks || []).forEach(task => {
      const key = task.promotion_id || `request-${task.request_id}`;
      if (!grouped[key]) grouped[key] = { ...task, tasks: [] };
      grouped[key].tasks.push(task);
    });
    return Object.values(grouped).filter(group => showCompleted || group.tasks.some(task => !task.completed));
  }, [data, showCompleted]);

  const toggle = async (task) => {
    setBusy(task.id);
    try {
      await api.patch(`/promotions/publication-tasks/${task.id}`, { completed: !task.completed });
      toast.success(task.completed ? 'Attività riaperta' : `${task.platform_label}: pubblicazione completata`);
      reload();
    } catch (error) { toast.error(error.response?.data?.detail || 'Impossibile aggiornare l’attività'); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="py-12 text-center text-slate-500" data-testid="platform-tasks-loading"><Loader2 className="inline h-5 w-5 animate-spin" /> Caricamento attività…</div>;
  return (
    <section className="space-y-4" data-testid="platform-checklist-container">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Pubblicazione sulle piattaforme</h2>
          <p className="text-sm text-slate-500">Canali centrali applicati automaticamente a ogni immobile.</p>
        </div>
        <Button variant="outline" onClick={() => setShowCompleted(value => !value)} data-testid="platform-show-completed-btn">
          {showCompleted ? 'Nascondi completate' : `Mostra completate (${data?.completed || 0})`}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="platform-default-channels">
        {(data?.platforms || []).map(platform => <span key={platform.key} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600" data-testid={`platform-default-${platform.key}`}>{platform.label}</span>)}
      </div>
      {!groups.length ? <p className="rounded-lg border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400" data-testid="platform-tasks-empty">Nessuna attività di pubblicazione da completare.</p> : (
        <div className="grid gap-3 xl:grid-cols-2">
          {groups.map(group => {
            const done = group.tasks.filter(task => task.completed).length;
            const waiting = group.tasks.some(task => task.state === 'waiting_activation');
            return <article key={group.promotion_id || group.request_id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={`platform-task-group-${group.promotion_id || group.request_id}`}>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div><p className="font-mono text-xs font-semibold text-slate-700">{group.property_code}</p>
                  <h3 className="font-semibold text-slate-900">{group.property_name} <span className="text-blue-700">-{group.pct}%</span></h3>
                  <p className="text-xs text-slate-500">Check-in {fmt(group.stay_from)} → {fmt(group.stay_to)}</p></div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${waiting ? 'bg-amber-100 text-amber-800' : done === group.tasks.length ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}
                  data-testid={`platform-task-status-${group.promotion_id || group.request_id}`}>
                  {waiting ? 'In attesa attivazione' : `${done}/${group.tasks.length} completate`}
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {group.tasks.map(task => <label key={task.id} className={`flex min-h-11 items-center justify-between gap-3 py-2 ${waiting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} data-testid={`platform-task-row-${task.id}`}>
                  <span className="flex items-center gap-2 text-sm text-slate-700">{task.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Clock3 className="h-4 w-4 text-amber-500" />}{task.platform_label}</span>
                  <input type="checkbox" checked={!!task.completed} disabled={waiting || busy === task.id} onChange={() => toggle(task)}
                    className="h-5 w-5 accent-emerald-600" data-testid={`platform-checkbox-${task.id}`} />
                </label>)}
              </div>
            </article>;
          })}
        </div>
      )}
    </section>
  );
};
